/*eslint-disable */
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  DocumentNode,
  fromPromise,
  InMemoryCache,
  NormalizedCacheObject,
  RequestHandler,
  split
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { gqlDoNothing, gqlUser } from "gql";
import { createClient } from "graphql-ws";
import Router from "next/router";
import { PUBLIC_ROUTES } from "routes/routes";
import { useUserStore } from "stores/user/userStore";
import { getQueryOperator } from "utils/helpers";
import {
  APPLICANT_TOKEN_PERSIST,
  GraphQLErrors,
  REFRESH_TOKEN_PERSIST,
  SCOPE,
  USER_LANG,
  USER_TOKEN_PERSIST
} from "./constants/global";

let apolloClient: ApolloClient<NormalizedCacheObject>;
let refreshPromise: Promise<unknown> | null = null;
const httpLink = createHttpLink({
  uri: `${process.env.NEXT_PUBLIC_APP_API_URL}/graphql`
});

let wsLink: ApolloLink | RequestHandler = ApolloLink.empty();
const isBrowser = typeof window !== "undefined";
if (isBrowser) {
  wsLink = new GraphQLWsLink(
    createClient({
      url: `${process.env.NEXT_PUBLIC_SUBSCRIPTIONS_URL}/subscriptions`,
      connectionParams: () => {
        const token = localStorage.getItem(USER_TOKEN_PERSIST);
        if (!token) return {};
        return {
          Authorization: `Bearer ${token}`,
          authorization: `Bearer ${token}`,
        };
      },
    })
  );
}

const authLink = setContext((context, params) => {
  const { headers } = params;

  return {
    headers: {
      Authorization: isBrowser
        ? `Bearer ${localStorage.getItem(USER_TOKEN_PERSIST)}`
        : null,
      "x-custom-lang": isBrowser ? localStorage.getItem(USER_LANG) : "en",
      hostname: isBrowser ? window.location.host : null,
      applicant: isBrowser ? localStorage.getItem(SCOPE) : null,
      applicantsession: isBrowser
        ? localStorage.getItem(APPLICANT_TOKEN_PERSIST)
        : null,
      ...headers
    }
  };
});

const DEFINITIVE_REFRESH_FAILURE_CODES = [
  "REFRESH_TOKEN_EXPIRED",
  "REFRESH_TOKEN_REVOKED",
  "REFRESH_TOKEN_MALFORMED",
  "INVALID_REFRESH_TOKEN"
] as const;

const isDefinitiveRefreshFailure = (error: unknown): boolean => {
  if (!localStorage.getItem(REFRESH_TOKEN_PERSIST)) {
    return true;
  }

  if (!error || typeof error !== "object") {
    return false;
  }

  const matchesCode = (value?: string) =>
    !!value &&
    DEFINITIVE_REFRESH_FAILURE_CODES.some(
      (code) => value === code || value.includes(code)
    );

  const apolloLike = error as {
    message?: string;
    graphQLErrors?: Array<{ message?: string; extensions?: { code?: string } }>;
    networkError?: { message?: string };
  };

  if (matchesCode(apolloLike.message)) {
    return true;
  }

  if (apolloLike.graphQLErrors?.length) {
    for (const gqlErr of apolloLike.graphQLErrors) {
      if (
        matchesCode(gqlErr.extensions?.code) ||
        matchesCode(gqlErr.message)
      ) {
        return true;
      }
    }
  }

  if (matchesCode(apolloLike.networkError?.message)) {
    return true;
  }

  return false;
};

const getNewToken = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_PERSIST);

      // If no refresh token exists, immediately reject
      if (!refreshToken) {
        reject(new Error("No refresh token available"));
        return;
      }

      const response = await apolloClient.mutate({
        mutation: gqlUser.mutations.REFRESH_ACCESS_TOKEN,
        variables: {
          refreshAccessTokenInput: {
            refreshToken
          }
        }
      });

      const { refreshAccessToken } = response.data;

      if (refreshAccessToken) {
        localStorage.setItem(
          USER_TOKEN_PERSIST,
          `${refreshAccessToken.accessToken}`
        );

        resolve(refreshAccessToken.accessToken);
      } else {
        reject(new Error("Failed to refresh token"));
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const signOutActions = () => {
  localStorage.removeItem(USER_TOKEN_PERSIST);
  localStorage.removeItem(REFRESH_TOKEN_PERSIST);

  // * Clear user from zustand store
  if (typeof window !== "undefined") {
    const { logout } = useUserStore.getState();
    logout();
  }
};

const onErrorLink = onError(
  ({ graphQLErrors, operation, forward, networkError }) => {
    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        switch (err.extensions?.code) {
          case GraphQLErrors.UNAUTHENTICATED:
            // Avoid recursion: refreshAccessToken itself returning UNAUTHENTICATED
            // means the session is dead — sign out once, do not start another refresh.
            if (operation.operationName === "refreshAccessToken") {
              signOutAndRedirect();
              return;
            }
            // Share ONE refresh across every UNAUTHENTICATED error that races in while
            // a refresh is already in flight. Concurrent ops await the same promise and
            // retry; we only sign out on definitive refresh-token auth failure.
            if (!refreshPromise) {
              refreshPromise = getNewToken()
                .catch((error) => {
                  if (isDefinitiveRefreshFailure(error)) {
                    signOutAndRedirect();
                  }
                  // Non-auth failures (network blip, 5xx, timeout): leave tokens alone
                  // so the next UNAUTHENTICATED can retry refresh. Returning undefined
                  // lets .filter(Boolean) drop the retry for this operation only.
                })
                .finally(() => {
                  refreshPromise = null;
                });
            }
            return fromPromise(refreshPromise)
              .filter((value) => Boolean(value))
              .flatMap((accessToken) => {
                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    Authorization: `Bearer ${accessToken}`
                  }
                });

                return forward(operation);
              });
          case GraphQLErrors.INTERNAL_SERVER_ERROR:
            console.error(err);
            break;
          case GraphQLErrors.GRAPHQL_VALIDATION_FAILED:
          case GraphQLErrors.GRAPHQL_PARSE_FAILED:
            console.error(err.message);
            break;
          default:
            if (err.extensions?.code === GraphQLErrors.BAD_USER_INPUT) {
              console.error(err.message);
              break;
            } else if (err.message) {
              console.error(err.message);
              break;
            } else {
              console.error(err);
              break;
            }
        }
      }
    }
  }
);

let splitLink: ApolloLink = httpLink;

if (isBrowser) {
  splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink
  );
}

const authFlowLink = authLink.concat(onErrorLink);
const link = authFlowLink.concat(splitLink || httpLink);

apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false
  }),
  link
});

export const forceRefetchQueries = async (queries: DocumentNode[]) => {
  // * Hack for run refetch queries by Name Without real mutation
  const queryNames = queries.map(getQueryOperator);

  return apolloClient.mutate({
    mutation: gqlDoNothing.mutations.DO_NOTHING,
    awaitRefetchQueries: true,
    refetchQueries: () => queryNames
  });
};

export const signOutAndRedirect = (redirectPath = PUBLIC_ROUTES.login.path) => {
  signOutActions();
  Router.push(redirectPath);
};

export default apolloClient;
